import { type AlertProps } from '@mui/material/Alert/Alert';
import darkScrollbar from '@mui/material/darkScrollbar';
import type { ColorSystemOptions, Overlays, Theme, ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';

import ArrowDown from '../../icons/ArrowDown';
import Check from '../../icons/Check';
import Error from '../../icons/Error';
import Info from '../../icons/Info';
import Warning from '../../icons/Warning';
import wdsFigmaExport from './figma-exports/default-theme/theme.json';

// augment the theme to add blue as a palette option
// see: https://mui.com/material-ui/customization/css-theme-variables/usage/#palette-interfaces
declare module '@mui/material/styles' {
    // Enable CSS theme variables support across the type system.
    // This enables typed colorSchemes, vars, forceThemeRerender, etc.
    // see: https://mui.com/material-ui/customization/css-theme-variables/configuration/
    interface CssThemeVariables {
        enabled: true;
    }
    interface PaletteOptions {
        blue?: string;
    }
    interface Palette {
        blue?: string;
    }

    interface PaletteColor {
        _states?: {
            hover: string;
            selected: string;

            focus: string;
            focusVisible: string;

            outlinedBorder?: string;
        };
    }
}

/**
 * Returns MUI overrides so that the Alert colours match Figma
 * @see https://www.figma.com/design/54pQgi6iKnhgI2REIUGekY/Seequent-Material-2-Design-Kit?node-id=10990-229266&t=zsy9NowuTKcuxpEN-0
 */
function fixAlertColours(severity: AlertProps['severity'], variant: AlertProps['variant']) {
    return ({ theme }: { theme: Theme }) => {
        if (!severity || !variant || !wdsFigmaExport) {
            // Just in case (keeping TSC happy)
            return {};
        }

        const { background, color } =
            wdsFigmaExport.colorSchemes[theme.palette.mode].palette._components.alert[severity];

        // Dark mode text colour is borked in the theme, so this overrides it to be the default text colour (white ish)
        const textColor = theme.palette.mode === 'light' ? color : theme.palette.text.primary;

        if (variant === 'outlined') {
            return {
                borderColor: theme.palette[severity].main,
                color: textColor,
            };
        }

        if (variant === 'filled') {
            return {
                backgroundImage: 'none',
            };
        }

        // Variant: standard
        return {
            backgroundColor: background,
            backgroundImage: 'none',
            color: textColor,
        };
    };
}

// Blue-tinted grey scale. The .light values are the canonical low-to-high
// (light-to-dark) ramp used for both palette.grey and evo-colors CSS variables.
// The .dark values are the semantically inverted equivalents used only by
// evo-colors (e.g. grey-700 dark = light colour for dark-mode foregrounds).
//
// palette.grey uses .light for BOTH modes so that MUI internals (Tooltip,
// Skeleton, Switch, etc.) that index by number always get the right shade.
const greys = {
    50: { light: '#FBFBFC', dark: '#1D1F23' },
    100: { light: '#F5F5F6', dark: '#3B3F47' },
    200: { light: '#ECEDF0', dark: '#575E68' },
    300: { light: '#DCDEE2', dark: '#696F7E' },
    400: { light: '#B6BAC2', dark: '#9499A5' },
    500: { light: '#9499A5', dark: '#B6BAC2' },
    600: { light: '#696F7E', dark: '#DCDEE2' },
    700: { light: '#575E68', dark: '#ECEDF0' },
    800: { light: '#3B3F47', dark: '#F5F5F6' },
    900: { light: '#1D1F23', dark: '#FBFBFC' },
} as const;

// Always use the .light (non-inverted) values for palette.grey so that low
// numbers are light and high numbers are dark regardless of colour scheme,
// which is what MUI components expect when they reference palette.grey[50], etc.
const paletteGrey = Object.fromEntries(
    Object.entries(greys).map(([key, value]) => [key, value.light]),
);

// Pre-computed blue-tinted elevation colours from the Figma export.
// Wrapped as linear-gradient so MUI applies them via background-image on Paper.
// MUI requires a fixed-length tuple of exactly 25 elements (elevations 0–24).
const darkOverlays = [
    'linear-gradient(#101114, #101114)', // 0
    'linear-gradient(#1B1D21, #1B1D21)', // 1
    'linear-gradient(#202226, #202226)', // 2
    'linear-gradient(#222428, #222428)', // 3
    'linear-gradient(#23262B, #23262B)', // 4
    'linear-gradient(#26292E, #26292E)', // 5
    'linear-gradient(#282B30, #282B30)', // 6
    'linear-gradient(#282B30, #282B30)', // 7
    'linear-gradient(#2A2D32, #2A2D32)', // 8
    'linear-gradient(#2A2D32, #2A2D32)', // 9
    'linear-gradient(#2D3035, #2D3035)', // 10
    'linear-gradient(#2D3035, #2D3035)', // 11
    'linear-gradient(#2E3138, #2E3138)', // 12
    'linear-gradient(#2E3138, #2E3138)', // 13
    'linear-gradient(#2E3138, #2E3138)', // 14
    'linear-gradient(#2E3138, #2E3138)', // 15
    'linear-gradient(#31343B, #31343B)', // 16
    'linear-gradient(#31343B, #31343B)', // 17
    'linear-gradient(#31343B, #31343B)', // 18
    'linear-gradient(#31343B, #31343B)', // 19
    'linear-gradient(#31343B, #31343B)', // 20
    'linear-gradient(#33363D, #33363D)', // 21
    'linear-gradient(#33363D, #33363D)', // 22
    'linear-gradient(#33363D, #33363D)', // 23
    'linear-gradient(#33363D, #33363D)', // 24
] as Overlays;

// a function to generate the custom colors the theme needs
function generateCustomColors() {
    const colors = {
        'blue-900': { light: '#265C7F', dark: '#E3F5F9' },
        'blue-800': { light: '#317BA1', dark: '#B8F6F0' },
        'blue-700': { light: '#358CB4', dark: '#8DD5E7' },
        'blue-600': { light: '#3E9FC7', dark: '#6AC5DE' },
        'blue-500': { light: '#46ACD5', dark: '#55B8D9' },
        'blue-400': { light: '#55B8D9', dark: '#46ACD5' },
        'blue-300': { light: '#6AC5DE', dark: '#3E9FC7' },
        'blue-200': { light: '#8DD5E7', dark: '#358CB4' },
        'blue-100': { light: '#B8E6F0', dark: '#317BA1' },
        'blue-50': { light: '#E3F5F9', dark: '#265C7F' },

        'grey-900': greys[900],
        'grey-800': greys[800],
        'grey-700': greys[700],
        'grey-600': greys[600],
        'grey-500': greys[500],
        'grey-400': greys[400],
        'grey-300': greys[300],
        'grey-200': greys[200],
        'grey-100': greys[100],
        'grey-50': greys[50],
    };

    const light: { [key: string]: string } = {};
    const dark: { [key: string]: string } = {};
    for (const [name, value] of [...Object.entries(colors)]) {
        light[`evo-colors-${name}`] = value.light;
        dark[`evo-colors-${name}`] = value.dark;
    }

    return { light, dark };
}

const fontFamily = 'Inter, "Helvetica Neue", Arial, sans-serif';

export const wdsTheme = createTheme({
    cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
    },
    typography: {
        ...wdsFigmaExport?.typography,
        fontFamily,
        button: {
            fontFamily,
        },
    },
    colorSchemes: {
        light: {
            palette: {
                ...(wdsFigmaExport?.colorSchemes?.light?.palette as ColorSystemOptions['palette']),
                grey: paletteGrey,
            },
            ...generateCustomColors().light,
        },
        dark: {
            palette: {
                ...(wdsFigmaExport?.colorSchemes?.dark?.palette as ColorSystemOptions['palette']),
                grey: paletteGrey,
                background: {
                    ...((wdsFigmaExport?.colorSchemes?.dark?.palette as Record<string, unknown>)
                        ?.background as Record<string, string>),
                    paper: '#101114',
                },
            },
            overlays: darkOverlays,
            ...generateCustomColors().dark,
        },
    },
    components: {
        MuiAccordion: {
            styleOverrides: {
                root: {
                    fontFamily,
                    width: '100%',
                },
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    flexGrow: 1,
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#696F7E',
                    fontFamily,
                    fontSize: wdsFigmaExport?.typography?.body2?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body2?.lineHeight,
                    fontWeight: 500,
                    width: '28px',
                    height: '28px',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontFamily,
                    textTransform: 'none',
                },
                sizeSmall: {
                    fontSize: wdsFigmaExport?.typography?.body2?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body2?.lineHeight,
                },
                sizeMedium: {
                    fontSize: wdsFigmaExport?.typography?.body1?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body1?.lineHeight,
                },
                sizeLarge: {
                    fontSize: wdsFigmaExport?.typography?.h5?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.h5?.lineHeight,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    fontFamily,
                },
                sizeSmall: {
                    fontSize: wdsFigmaExport?.typography?.body2?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body2?.lineHeight,
                },
                sizeMedium: {
                    fontSize: wdsFigmaExport?.typography?.body1?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body1?.lineHeight,
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontSize: wdsFigmaExport?.typography?.body2?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body2?.lineHeight,
                },
            },
        },
        MuiSelect: {
            defaultProps: { IconComponent: ArrowDown },
        },
        MuiAutocomplete: {
            defaultProps: { popupIcon: <ArrowDown fontSize="small" sx={{ marginTop: '2px' }} /> },
        },
        MuiCssBaseline: {
            styleOverrides: (themeParam: Theme) => ({
                body: themeParam.palette.mode === 'dark' ? darkScrollbar() : null,
                div: {
                    scrollbarWidth: 'thin !important' as const,
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: themeParam.palette.text.disabled,
                        borderRadius: themeParam.spacing(1.25),
                        '--webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.0)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: themeParam.palette.text.secondary,
                        borderRadius: themeParam.spacing(1.25),
                        '--webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.3)',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: themeParam.palette.text.primary,
                    },
                },
            }),
        },
        MuiAlert: {
            styleOverrides: {
                standardInfo: fixAlertColours('info', 'standard'),
                standardWarning: fixAlertColours('warning', 'standard'),
                standardSuccess: fixAlertColours('success', 'standard'),
                standardError: fixAlertColours('error', 'standard'),
                outlinedInfo: fixAlertColours('info', 'outlined'),
                outlinedWarning: fixAlertColours('warning', 'outlined'),
                outlinedSuccess: fixAlertColours('success', 'outlined'),
                outlinedError: fixAlertColours('error', 'outlined'),
                filledInfo: fixAlertColours('info', 'filled'),
                filledWarning: fixAlertColours('warning', 'filled'),
                filledSuccess: fixAlertColours('success', 'filled'),
                filledError: fixAlertColours('error', 'filled'),
                message: {
                    width: '100%',
                    fontSize: wdsFigmaExport?.typography?.body1?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body1?.lineHeight,
                    padding: '10px 0',
                },
            },
            defaultProps: {
                iconMapping: {
                    error: <Error />,
                    warning: <Warning />,
                    success: <Check />,
                    info: <Info />,
                },
            },
        },
        MuiAlertTitle: {
            styleOverrides: {
                root: {
                    ...wdsFigmaExport?.typography?.h6,
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    padding: theme.spacing(2, 3, 3, 3),
                }),
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    padding: theme.spacing(3, 3, 2, 3),
                }),
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    fontFamily,
                    '& ::placeholder': {
                        color: theme.palette.text.secondary,
                    },
                }),
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    borderColor: theme.vars.palette.divider,
                }),
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }: { theme: Theme }) => ({
                    fontFamily,
                    textTransform: 'none',
                    padding: theme.spacing(0.5, 1),
                    fontWeight: 500,
                }),
                sizeSmall: {
                    fontSize: wdsFigmaExport?.typography?.body2?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body2?.lineHeight,
                },
                sizeMedium: {
                    fontSize: wdsFigmaExport?.typography?.body1?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.body1?.lineHeight,
                },
                sizeLarge: {
                    fontSize: wdsFigmaExport?.typography?.h5?.fontSize,
                    lineHeight: wdsFigmaExport?.typography?.h5?.lineHeight,
                },
            },
        },
    },
} as ThemeOptions);
